import { UserGuard } from '../core/guards/user.guard';
import { Routes } from '@angular/router';
import { RoleAdminGuard } from '../core/guards/role.admin.guard';
import { RoleAdminOrManagerGuard } from '../core/guards/role.admin.manager.guard';
import { GlobalParamsGuard } from '../core/guards/global-params.guard';
import { MerchantHasSetupGuardGuard } from '../core/guards/merchant-has-setup-guard.guard';
import { InvoiceAddItemsComponent } from './invoicing/invoice-add-items/invoice-add-items.component';
import { InvoicePaymentMethodComponent } from './invoicing/invoice-payment-method/invoice-payment-method.component';
import { InvoiceSuccessComponent } from './invoicing/invoice-success/invoice-success.component';
import { SalesListPageComponent } from './invoicing/sales-list-page/sales-list-page.component';
import { PaymentsListPageComponent } from './invoicing/payments-list-page/payments-list-page.component';
import { CommissionListPageComponent } from './invoicing/commission-list-page/commission-list-page.component';
import { InvoicingPageComponent } from './invoicing/invoicing-page/invoicing-page.component';
import { salesModuleActive } from '../core/guards/sales-module-active.guard';
import { userPermissionGuard } from '../core/guards/user-permission.guard';
import { CheckoutMode } from '../core/services/invoicing.service';
import { ShowReportsGuard } from '../core/guards/show-reports.guard';
import { showClientsGuard } from '../core/guards/show-clients.guard';

export const dashboardRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [UserGuard, GlobalParamsGuard, MerchantHasSetupGuardGuard],
    canActivateChild: [GlobalParamsGuard],
    children: [
      { path: '', redirectTo: 'calendar', pathMatch: 'prefix' },
      {
        path: 'providers',
        pathMatch: 'full',
        canActivate: [RoleAdminOrManagerGuard],
        loadComponent: () => import('./providers-page/providers-page.component').then((m) => m.ProvidersPageComponent),
      },
      {
        path: 'users',
        pathMatch: 'full',
        canActivate: [RoleAdminGuard],
        loadComponent: () => import('./users-page/users-page.component').then((m) => m.UsersPageComponent),
      },
      {
        path: 'services',
        pathMatch: 'full',
        canActivate: [RoleAdminOrManagerGuard],
        loadComponent: () => import('./services-page/services-page.component').then((m) => m.ServicesPageComponent),
      },
      {
        path: 'products',
        pathMatch: 'full',
        canActivate: [salesModuleActive, RoleAdminOrManagerGuard],
        loadComponent: () => import('./products-page/products-page.component').then((m) => m.ProductsPageComponent),
      },
      {
        path: 'rooms',
        pathMatch: 'full',
        canActivate: [RoleAdminOrManagerGuard],
        loadComponent: () => import('./rooms-page/rooms-page.component').then((m) => m.RoomsPageComponent),
      },
      {
        path: 'schedule',
        pathMatch: 'full',
        loadComponent: () => import('./workplan-page/workplan-page.component').then((m) => m.WorkplanPageComponent),
      },
      {
        path: 'workplan-override',
        pathMatch: 'full',
        loadComponent: () => import('./workplan-override/workplan-override.component').then((m) => m.WorkplanOverrideComponent),
      },
      { path: 'payment', component: InvoicingPageComponent, canActivate: [salesModuleActive, userPermissionGuard('canCreateEditSales')], children: [
        {path: '', redirectTo: 'create', pathMatch: 'full'},
        {path: 'create', pathMatch: 'full', component: InvoiceAddItemsComponent, data: { invoiceCreationMode: CheckoutMode.CREATE_FULL_SALE }},
        {path: 'edit/:saleId', pathMatch: 'full', component: InvoiceAddItemsComponent, data: { invoiceCreationMode: CheckoutMode.EDIT_SALE }},
        {path: 'complete/:saleId', pathMatch: 'full', component: InvoiceAddItemsComponent, data: { invoiceCreationMode: CheckoutMode.INVOICE_REMAINING }},
        {path: 'pay/:saleId', pathMatch: 'full', component: InvoicePaymentMethodComponent, data: { invoiceCreationMode: CheckoutMode.ADD_PAYMENT }},
        {path: 'add-invoice/:saleId', pathMatch: 'full', component: InvoicePaymentMethodComponent, data: { invoiceCreationMode: CheckoutMode.INVOICE_REMAINING }},
        {path: 'payment-method', pathMatch: 'full', component: InvoicePaymentMethodComponent},
        {path: 'teya', pathMatch: 'full', component: InvoiceSuccessComponent, data: { teyaTerminal: true }},
        {path: 'success', pathMatch: 'full', component: InvoiceSuccessComponent},
        {path: 'payment-success', pathMatch: 'full', component: InvoiceSuccessComponent, data: { paymentMode: true }},
      ]},
      {
        path: 'calendar',
        pathMatch: 'full',
        loadComponent: () => import('./new-calendar-page/new-calendar-page.component').then((m) => m.NewCalendarPageComponent),
      },
      { path: 'sales', component: SalesListPageComponent, canActivate: [salesModuleActive, userPermissionGuard('canViewSales')] },
      { path: 'sales/payments', component: PaymentsListPageComponent, canActivate: [salesModuleActive, userPermissionGuard('canViewSales')] },
      { path: 'sales/commissions', component: CommissionListPageComponent, canActivate: [salesModuleActive, userPermissionGuard('canViewSales')] },
      {
        path: 'welcome',
        pathMatch: 'full',
        loadComponent: () => import('./new-calendar-page/new-calendar-page.component').then((m) => m.NewCalendarPageComponent),
      },
      {
        path: 'calendar/week',
        pathMatch: 'full',
        data: { threeDayView: false },
        loadComponent: () =>
          import('./new-calendar-week-page/new-calendar-week-page.component').then((m) => m.NewCalendarWeekPageComponent),
      },
      {
        path: 'calendar/three-day',
        pathMatch: 'full',
        data: { threeDayView: true },
        loadComponent: () =>
          import('./new-calendar-week-page/new-calendar-week-page.component').then((m) => m.NewCalendarWeekPageComponent),
      },
      {
        path: 'clients',
        pathMatch: 'full',
        canActivate: [showClientsGuard],
        loadComponent: () => import('./clients-page/clients-page.component').then((m) => m.ClientsPageComponent),
        data: { clientType: 'clients' },
      },
      {
        path: 'clients/blacklist',
        pathMatch: 'full',
        canActivate: [showClientsGuard],
        loadComponent: () => import('./clients-page/clients-page.component').then((m) => m.ClientsPageComponent),
        data: { clientType: 'blacklist' },
      },
      {
        path: 'settings',
        pathMatch: 'full',
        canActivate: [RoleAdminOrManagerGuard],
        loadComponent: () => import('./store-settings-page/store-settings-page.component').then((m) => m.StoreSettingsPageComponent),
      },
      {
        path: 'settings/custom-fields',
        pathMatch: 'full',
        canActivate: [RoleAdminGuard],
        loadComponent: () =>
          import('./setup-custom-fields-page/setup-custom-fields-page.component').then((m) => m.SetupCustomFieldsPageComponent),
      },
      {
        path: 'account',
        pathMatch: 'full',
        canActivate: [],
        loadComponent: () => import('./account-settings-page/account-settings-page.component').then((m) => m.AccountSettingsPageComponent),
      },
      {
        path: 'referral',
        pathMatch: 'full',
        canActivate: [RoleAdminGuard],
        loadComponent: () => import('./referral-page/referral-page.component').then((m) => m.ReferralPageComponent),
      },
      {
        path: 'invoices',
        pathMatch: 'full',
        canActivate: [RoleAdminGuard],
        loadComponent: () => import('./account-invoices-page/account-invoices-page.component').then((m) => m.AccountInvoicesPageComponent),
      },
      {
        path: 'marketing',
        canActivate: [RoleAdminOrManagerGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./marketing-page-v2/marketing-page-v2.component').then((m) => m.MarketingPageV2Component),
          },
          {
            path: 'new-sms-campaign',
            loadComponent: () => import('./sms-campaign-page/sms-campaign-page.component').then((m) => m.SmsCampaignPageComponent),
          },
          {
            path: 'new-email-campaign',
            loadComponent: () => import('./email-campaign-page/email-campaign-page.component').then((m) => m.EmailCampaignPageComponent),
          },
          {
            path: 'campaign/sms/:id',
            loadComponent: () => import('./sms-campaign-page/sms-campaign-page.component').then((m) => m.SmsCampaignPageComponent),
          },
          {
            path: 'campaign/email/:id',
            loadComponent: () => import('./email-campaign-page/email-campaign-page.component').then((m) => m.EmailCampaignPageComponent),
          },
        ],
      },
      {
        path: 'subscription',
        pathMatch: 'full',
        loadComponent: () =>
          import('./store-subscription-page/store-subscription-page.component').then((m) => m.StoreSubscriptionPageComponent),
      },
      // { path: 'discount/code', pathMatch: 'full', loadComponent: () => import('./discount-code-page/discount-code-page.component').then(m => m.DiscountCodePageComponent) },
      {
        path: 'reports',
        canActivate: [ShowReportsGuard],
        pathMatch: 'full',
        loadComponent: () => import('./reports/reports-list/reports-list.component').then((m) => m.ReportsListComponent),
      },
      {
        path: 'reports/general',
        canActivate: [ShowReportsGuard],
        pathMatch: 'full',
        loadComponent: () => import('./stats-page/stats-page.component').then((m) => m.StatsPageComponent),
      },
      {
        path: 'reports/:reportType',
        canActivate: [ShowReportsGuard],
        pathMatch: 'full',
        loadComponent: () => import('./reports/report-page/report-page.component').then((m) => m.ReportPageComponent),
      },
      {
        path: 'extensions',
        pathMatch: 'full',
        loadComponent: () => import('./extensions-page/extensions-page.component').then((m) => m.ExtensionsPageComponent),
      },
      {
        path: 'messaging',
        pathMatch: 'full',
        loadComponent: () => import('./messaging-page/messaging-page.component').then((m) => m.MessagingPageComponent),
      },
      {
        path: 'addons/google',
        pathMatch: 'full',
        loadComponent: () => import('./google-place-setup/google-place-setup.component').then((m) => m.GooglePlaceSetupComponent),
      },
      {
        path: 'extensions/teya',
        pathMatch: 'full',
        loadComponent: () => import('./teya-setup/teya-setup.component').then((m) => m.TeyaSetupComponent),
      },
      {
        path: 'extensions/google-meet',
        pathMatch: 'full',
        canActivate: [RoleAdminOrManagerGuard],
        loadComponent: () => import('./google-meet-setup/google-meet-setup.component').then((m) => m.GoogleMeetSetupComponent),
      },
      {
        path: 'extensions/google',
        pathMatch: 'full',
        loadComponent: () => import('./google-place-setup/google-place-setup.component').then((m) => m.GooglePlaceSetupComponent),
      },
      {
        path: 'addons/google-reviews',
        pathMatch: 'full',
        loadComponent: () => import('./google-reviews-setup/google-reviews-setup.component').then((m) => m.GoogleReviewsSetupComponent),
      },
      {
        path: 'extensions/google-reviews',
        pathMatch: 'full',
        loadComponent: () => import('./google-reviews-setup/google-reviews-setup.component').then((m) => m.GoogleReviewsSetupComponent),
      },
      {
        path: 'addons/loyalty',
        pathMatch: 'full',
        loadComponent: () => import('./loyalty-program-page/loyalty-program-page.component').then((m) => m.LoyaltyProgramPageComponent),
      },
      {
        path: 'extensions/loyalty',
        pathMatch: 'full',
        loadComponent: () => import('./loyalty-program-page/loyalty-program-page.component').then((m) => m.LoyaltyProgramPageComponent),
      },
      {
        path: 'addons/rgpd',
        pathMatch: 'full',
        loadComponent: () => import('./rgpd-page/rgpd-page.component').then((m) => m.RgpdPageComponent),
      },
      {
        path: 'extensions/moloni',
        pathMatch: 'full',
        loadComponent: () => import('./setup-moloni/setup-moloni.component').then((m) => m.SetupMoloniComponent),
      },
      {
        path: 'extensions/rgpd',
        pathMatch: 'full',
        loadComponent: () => import('./rgpd-page/rgpd-page.component').then((m) => m.RgpdPageComponent),
      },
      {
        path: 'addons/embed',
        pathMatch: 'full',
        loadComponent: () => import('./embed-code-setup/embed-code-setup.component').then((m) => m.EmbedCodeSetupComponent),
      },
      {
        path: 'extensions/embed',
        pathMatch: 'full',
        loadComponent: () => import('./embed-code-setup/embed-code-setup.component').then((m) => m.EmbedCodeSetupComponent),
      },
      {
        path: 'extensions/payments',
        pathMatch: 'full',
        loadComponent: () => import('./payments-setup/payments-setup.component').then((m) => m.PaymentsSetupComponent),
      },
      {
        path: 'extensions/sales',
        pathMatch: 'full',
        loadComponent: () => import('./invoicing/sales-setup-page/sales-setup-page.component').then((m) => m.SalesSetupPageComponent),
      },
      {
        path: 'extensions/invoicing',
        pathMatch: 'full',
        loadComponent: () => import('./invoicing/invoicing-setup-page/invoicing-setup-page.component').then((m) => m.InvoicingSetupPageComponent),
      },
      {
        path: 'extensions/invoicing/setup',
        pathMatch: 'full',
        loadComponent: () => import('./invoicing/invoicing-onboard-setup-page/invoicing-onboard-setup-page.component').then((m) => m.InvoicingOnboardSetupPageComponent),
      },
      {
        // dummy component to handle redirection between same component with different data (create sale from existing sale) - forces full destroy and re-init of invoicing page, for exemple
        path: 'redirect',
        pathMatch: 'full',
        loadComponent: () => import('./redirect/redirect.component').then((m) => m.RedirectComponent),
      }
    ],
  },
];
